import { useCallback, useEffect, useRef, useState } from 'react';
import ChevronIcon from './ChevronIcon';

interface TaskSpecProps {
  prompt: string;
  workstreamID: string;
  taskID: string;
  version: number;
  onSaved?: (newPrompt: string, newVersion: number) => void;
}

export default function TaskSpec({
  prompt,
  workstreamID,
  taskID,
  version,
  onSaved,
}: TaskSpecProps) {
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(prompt);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasSpec = !!prompt.trim();

  useEffect(() => {
    setDraft(prompt);
  }, [prompt]);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [editing]);

  const save = useCallback(async () => {
    if (draft === prompt) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const resp = await fetch(
        `/api/workstreams/${encodeURIComponent(workstreamID)}/tasks/${encodeURIComponent(taskID)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Version: version, Fields: { Prompt: draft } }),
        },
      );
      if (resp.ok) {
        const data = await resp.json();
        onSaved?.(data.Prompt ?? draft, data.Version);
      }
    } catch {
      // silently fail
    } finally {
      setSaving(false);
      setEditing(false);
      if (draft.trim()) setExpanded(true);
    }
  }, [draft, prompt, workstreamID, taskID, version, onSaved]);

  const cancel = useCallback(() => {
    setDraft(prompt);
    setEditing(false);
  }, [prompt]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
      }
    },
    [cancel],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      if (containerRef.current?.contains(e.relatedTarget as Node)) return;
      save();
    },
    [save],
  );

  const startEditing = () => {
    setEditing(true);
    setExpanded(true);
  };

  // No spec — show "click to define" prompt
  if (!hasSpec && !editing) {
    return (
      <div style={{ marginTop: '16px' }}>
        <span
          role="button"
          tabIndex={0}
          onClick={startEditing}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              startEditing();
            }
          }}
          style={{
            cursor: 'pointer',
            color: '#656d76',
            fontStyle: 'italic',
            fontSize: '14px',
          }}
        >
          Click to define the task spec
        </span>
      </div>
    );
  }

  // Editing mode
  if (editing) {
    return (
      <div ref={containerRef} style={{ marginTop: '16px' }} onBlur={handleBlur}>
        <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
          Task Spec
        </div>
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={saving}
          rows={8}
          style={{
            width: '100%',
            maxWidth: '800px',
            fontSize: '14px',
            padding: '8px',
            border: '1px solid #ccc',
            borderRadius: '6px',
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <button
            type="button"
            className="btn-small"
            disabled={saving}
            onClick={save}
          >
            Save
          </button>
          <button
            type="button"
            className="btn-small btn-secondary"
            disabled={saving}
            onClick={cancel}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // View mode — collapsible with chevron
  return (
    <div style={{ marginTop: '16px' }}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((e) => !e)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((ex) => !ex);
          }
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          cursor: 'pointer',
          fontWeight: 600,
          fontSize: '14px',
          userSelect: 'none',
        }}
      >
        <span style={{
          display: 'inline-block',
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s',
        }}>
          <ChevronIcon size={16} />
        </span>
        Task Spec
      </div>
      {expanded && (
        <div
          role="button"
          tabIndex={0}
          onClick={startEditing}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              startEditing();
            }
          }}
          style={{
            marginTop: '8px',
            fontSize: '14px',
            lineHeight: 1.6,
            cursor: 'pointer',
            whiteSpace: 'pre-wrap',
          }}
        >
          {prompt}
        </div>
      )}
    </div>
  );
}
